import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  Router,
  RouterStateSnapshot,
  UrlTree
} from '@angular/router';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AuthService } from '../services/auth/auth-service';
import { ROLE_PARTB } from '../constants/roles';

/**
 * Guard for the root path ('').
 *
 * Sends Part B operators *without* homepage access to `/part-b` so they
 * don't have to discover the route via the user menu. Users who *do*
 * have homepage access — `super_mario` (back-office), `Dashboard`
 * (Lesotho2 homepage features), and any other role in
 * `HOMEPAGE_ACCESS_ROLES` — keep landing on `/`. Everyone else
 * (applicants, superinspector-only, sysadmin-only, anonymous) is also
 * unaffected.
 *
 * Redirect predicate: `partb AND NOT (any HOMEPAGE_ACCESS_ROLES role)`.
 * Distinct from `restrictPartBToOwnServices$` (the services-filter
 * predicate) — those decisions diverge for users like the Lesotho2
 * registrar (holding the `Dashboard` role), who is an operator AND has
 * homepage features.
 *
 * Fails open on errors so a transient role-API hiccup never traps a
 * user on `/`.
 */
@Injectable({
  providedIn: 'root'
})
export class PartBLandingGuard implements CanActivate {
  constructor(
    private readonly router: Router,
    private readonly authService: AuthService
  ) {}

  canActivate(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _route: ActivatedRouteSnapshot,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> {
    return forkJoin([
      this.authService.isUserInRole(ROLE_PARTB),
      this.authService.hasHomepageAccess$()
    ]).pipe(
      map(([isPartB, hasHomepageAccess]) =>
        isPartB && !hasHomepageAccess ? this.router.parseUrl('/part-b') : true
      ),
      catchError(() => of(true))
    );
  }
}
